import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  List,
  type LucideIconData,
} from 'lucide-angular';
import { catchError, combineLatest, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';

import { IconComponent } from '../../shared/ui/icon/icon.component';
import { SeoService } from '../../shared/services/seo.service';
import { GameSectionService } from '../game-section/data/game-section.service';
import { publicGameSummaryToUi } from '../game-section/data/game-section.mappers';
import { EventsService } from './data/events.service';
import type { StoreEvent } from './data/events.model';
import { EventCardComponent } from './components/event-card/event-card.component';
import { EventsCalendarComponent } from './components/events-calendar/events-calendar.component';
import { currentMonthKey, monthLabel, shiftMonth } from './utils/event-format.util';
import { EVENTS_COPY } from './constants/events.constants';

/** `lista` es el modo por defecto: es el que responde "¿qué hay pronto?". */
export type EventsViewMode = 'lista' | 'calendario';

type EventsStatus = 'loading' | 'ready' | 'error';

/**
 * Página de eventos. Sirve **dos rutas** con el mismo componente:
 *
 * - `/juegos/{slug}/eventos` — listado completo de un juego, del que la landing
 *   solo enseña los 6 primeros.
 * - `/eventos` — agenda global, con filtro opcional por juego.
 *
 * La diferencia no es cosmética, viene de qué ofrece cada endpoint. El listado
 * por juego (`/web/games/{slug}/events`) devuelve **todos** los eventos no
 * terminados, sin mes; el calendario (`/web/events/calendar`) trabaja **por
 * mes**. Así que la vista de lista de un juego enseña todo lo que viene, y la
 * global enseña el mes seleccionado — que es también lo único que un calendario
 * global puede enseñar—. Por eso el navegador de mes aparece en los dos modos
 * de la página global y solo en el calendario de la de juego.
 *
 * Ante un fallo del endpoint la página se queda en su estado de error y no
 * propaga: es una página propia, no un bloque de otra, pero el criterio es el
 * mismo que en la landing.
 */
@Component({
  selector: 'app-events-page',
  imports: [IconComponent, EventCardComponent, EventsCalendarComponent, RouterLink],
  templateUrl: './events-page.component.html',
  styleUrl: './events-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class EventsPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventsService = inject(EventsService);
  private readonly gameSectionService = inject(GameSectionService);
  private readonly seo = inject(SeoService);

  protected readonly copy = EVENTS_COPY;
  protected readonly listIcon: LucideIconData = List;
  protected readonly calendarIcon: LucideIconData = CalendarDays;
  protected readonly chevronLeftIcon: LucideIconData = ChevronLeft;
  protected readonly chevronRightIcon: LucideIconData = ChevronRight;

  protected readonly events = signal<readonly StoreEvent[]>([]);
  protected readonly status = signal<EventsStatus>('loading');

  protected readonly skeletonRows: readonly number[] = [0, 1, 2, 3];
  /** 5 semanas × 7 días: el alto más frecuente de una rejilla mensual. */
  protected readonly skeletonCells: readonly number[] = Array.from({ length: 35 }, (_, i) => i);

  /** Slug de la ruta: presente solo en `/juegos/{slug}/eventos`. */
  protected readonly routeGameSlug = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('gameSlug'))),
    { initialValue: null },
  );

  private readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: null });

  protected readonly mode = computed<EventsViewMode>(() =>
    this.queryParams()?.get('vista') === 'calendario' ? 'calendario' : 'lista',
  );

  protected readonly month = computed(
    () => this.queryParams()?.get('mes') || currentMonthKey(),
  );

  /** Filtro de la página global. En la de juego manda siempre la ruta. */
  protected readonly filterGameSlug = computed(
    () => this.routeGameSlug() ?? this.queryParams()?.get('juego') ?? null,
  );

  protected readonly isGamePage = computed(() => this.routeGameSlug() !== null);

  /**
   * Índice de juegos para el selector y para rotular el título con el nombre
   * real. Degrada a lista vacía: quedarse sin el índice recorta el filtro, no
   * la página —el mismo criterio que el megamenú del header—.
   */
  private readonly games = toSignal(
    this.gameSectionService.getGames().pipe(
      map((rows) => rows.map(publicGameSummaryToUi)),
      catchError((err: unknown) => {
        console.error('[eventos] games index fetch failed', err);
        return of([]);
      }),
    ),
    { initialValue: [] },
  );

  protected readonly gameOptions = computed(() =>
    this.games().map((game) => ({ slug: game.slug, name: game.shortName || game.name })),
  );

  protected readonly gameName = computed(() => {
    const slug = this.filterGameSlug();
    if (!slug) return null;
    return this.games().find((game) => game.slug === slug)?.name ?? slug;
  });

  protected readonly heading = computed(() => {
    const name = this.gameName();
    return name ? `Eventos de ${name}` : this.copy.globalTitle;
  });

  protected readonly monthHeading = computed(() => monthLabel(this.month()));

  /**
   * El navegador de mes gobierna la vista salvo en la lista de un juego, que
   * enseña todo lo que viene sin recortar por mes.
   */
  protected readonly showsMonthNav = computed(
    () => this.mode() === 'calendario' || !this.isGamePage(),
  );

  protected readonly isEmpty = computed(
    () => this.status() === 'ready' && this.events().length === 0,
  );

  protected readonly emptyMessage = computed(() => {
    if (this.isGamePage() && this.mode() === 'lista') {
      return this.copy.emptyGame;
    }
    return `${this.copy.emptyMonth} ${this.monthHeading()}.`;
  });

  constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([params, query]) => ({
          gameSlug: params.get('gameSlug'),
          mode: query.get('vista') === 'calendario' ? ('calendario' as const) : ('lista' as const),
          month: query.get('mes') || currentMonthKey(),
          filterGame: params.get('gameSlug') ?? query.get('juego'),
        })),
        // Sin esto, tocar un query param irrelevante volvería a pedir lo mismo.
        distinctUntilChanged(
          (a, b) =>
            a.gameSlug === b.gameSlug &&
            a.mode === b.mode &&
            a.month === b.month &&
            a.filterGame === b.filterGame,
        ),
        tap(() => this.status.set('loading')),
        switchMap((state) =>
          this.load(state).pipe(
            map((events) => ({ kind: 'ready' as const, events })),
            catchError((err: unknown) => {
              console.error('[eventos] events fetch failed', err);
              return of({ kind: 'error' as const });
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result.kind === 'error') {
          this.events.set([]);
          this.status.set('error');
          return;
        }
        this.events.set(result.events);
        this.status.set('ready');
      });

    this.applySeo();
  }

  private load(state: {
    gameSlug: string | null;
    mode: EventsViewMode;
    month: string;
    filterGame: string | null;
  }) {
    // La lista de un juego usa el listado completo; todo lo demás, el mes.
    if (state.mode === 'lista' && state.gameSlug) {
      return this.eventsService.getByGame(state.gameSlug);
    }
    return this.eventsService.getCalendar(state.month, state.filterGame ?? undefined);
  }

  protected setMode(mode: EventsViewMode): void {
    // El modo vive en la URL para que la vista sobreviva a un refresco y sea
    // enlazable: "mira el calendario de junio" es un enlace, no una instrucción.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { vista: mode === 'calendario' ? 'calendario' : null },
      queryParamsHandling: 'merge',
    });
  }

  protected setMonth(month: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mes: month === currentMonthKey() ? null : month },
      queryParamsHandling: 'merge',
    });
  }

  protected shiftMonthBy(delta: number): void {
    this.setMonth(shiftMonth(this.month(), delta));
  }

  protected onGameFilterChange(event: Event): void {
    const slug = (event.target as HTMLSelectElement).value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { juego: slug || null },
      queryParamsHandling: 'merge',
    });
  }

  private applySeo(): void {
    this.seo.apply({
      title: this.copy.globalTitle,
      description: this.copy.seoDescription,
      type: 'website',
      canonical: '/eventos',
    });
  }
}
